
import React from 'react';
import { CardHeader, CardFooter, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Sparkles, Star, Clock, Trophy } from 'lucide-react';
import { Avatar } from '@/components/ui/avatar';
import { AIStaffMember } from '@/lib/types';
import { BrandCard, BrandText, BrandIcon } from '@/components/ui/brand';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface AIStaffCardProps {
  staff: AIStaffMember;
  onClick?: () => void;
}

const AIStaffCard: React.FC<AIStaffCardProps> = ({ staff, onClick }) => {
  const getStaffLevelColor = () => {
    const level = staff.level || 'intern';
    const colorMap: Record<string, string> = {
      'intern': 'bg-blue-500',
      'junior': 'bg-green-500',
      'mid': 'bg-yellow-500',
      'senior': 'bg-purple-500',
      'director': 'bg-red-500'
    };
    return colorMap[level] || 'bg-blue-500';
  };

  // Format the hired date
  const formattedDate = staff.hiredAt 
    ? format(new Date(staff.hiredAt), 'MMM d, yyyy') 
    : 'Recently hired';

  // Extract experience values with fallbacks
  const experienceCurrent = typeof staff.experience === 'number' 
    ? staff.experience 
    : (staff.experience as any)?.current || 0;
  
  const experienceRequired = (staff.experience as any)?.required || 100;
  
  // Calculate progress percentage safely
  const progressPercentage = (experienceCurrent / experienceRequired) * 100;

  return (
    <BrandCard 
      className="cursor-pointer transition-all hover:-translate-y-1 hover:shadow-md overflow-hidden h-full"
      onClick={onClick}
      animation="fade-in"
    >
      <div className="absolute top-0 right-0 p-2">
        <div className={cn("px-2 py-1 rounded-full text-xs font-medium text-white", getStaffLevelColor())}>
          {(staff.level || 'intern').charAt(0).toUpperCase() + (staff.level || 'intern').slice(1)}
        </div>
      </div>
      
      <CardHeader className="flex flex-col items-center text-center pt-8">
        <Avatar className="h-20 w-20 mb-4">
          <div className="h-full w-full rounded-full overflow-hidden bg-primary/10 flex items-center justify-center">
            {staff.avatar ? (
              <img src={staff.avatar} alt={staff.name} className="h-full w-full object-cover" />
            ) : (
              <BrandIcon icon={Sparkles} size="xl" color="primary" />
            )}
          </div>
        </Avatar>
        
        <CardTitle className="mb-1">{staff.name}</CardTitle>
        <BrandText size="sm" muted className="mb-2">{staff.role || 'Marketing Assistant'}</BrandText>
        
        <div className="flex space-x-1 mb-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star 
              key={i} 
              className={cn(
                "h-4 w-4", 
                i < ((staff as any).rating || 0) ? "text-yellow-400 fill-yellow-400" : "text-gray-300"
              )} 
            />
          ))}
        </div>
      </CardHeader>
      
      <div className="px-6 pb-6">
        <div className="space-y-4">
          <div className="flex items-center text-sm">
            <Clock className="h-4 w-4 mr-2 text-muted-foreground" />
            <BrandText size="sm" muted>Hired on {formattedDate}</BrandText>
          </div>
          
          <div className="flex items-center text-sm">
            <Trophy className="h-4 w-4 mr-2 text-muted-foreground" />
            <BrandText size="sm" muted>{staff.achievements?.length || 0} Achievements</BrandText>
          </div>
          
          <div className="mt-4">
            <div className="flex justify-between mb-1">
              <BrandText size="sm" muted>Experience</BrandText>
              <BrandText size="sm">{experienceCurrent}/{experienceRequired}</BrandText>
            </div>
            <Progress className="h-2" value={progressPercentage} />
          </div>
        </div>
      </div>
      
      <CardFooter className="bg-muted/30 px-6 py-3 border-t flex justify-between">
        <BrandText size="sm">View Details</BrandText>
        <BrandIcon icon={Sparkles} size="sm" color="primary" />
      </CardFooter>
    </BrandCard>
  );
};

export default AIStaffCard;
